import type { CSSProperties, ReactNode } from 'react';

interface ProgressRingProps {
  value: number;
  max: number;
  size?: number;
  stroke?: number;
  color?: string;
  trackColor?: string;
  children?: ReactNode;
  ariaLabel?: string;
}

export function ProgressRing({
  value,
  max,
  size = 72,
  stroke = 7,
  color = 'var(--vine)',
  trackColor = 'var(--line)',
  children,
  ariaLabel,
}: ProgressRingProps) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const offset = circumference * (1 - ratio);
  const wrap: CSSProperties = { position: 'relative', width: size, height: size, flexShrink: 0 };

  return (
    <div style={wrap} role={ariaLabel ? 'img' : undefined} aria-label={ariaLabel}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset var(--dur) var(--ease-out)' }}
        />
      </svg>
      {children != null ? (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
